/**
 * SectionHeader — Apple Settings-style section header with vivid colored icon + bar.
 *
 * Used above every card section in the Item Detail experience view.
 * Each section gets a unique icon and color from the SECTION_META registry.
 * Falls back to a neutral gray style for unknown sections.
 */

import type { DetailTextSize } from "@/lib/store/preferencesStore";
import type { LucideIcon } from "lucide-react";
import { useLanguage } from "@/i18n/useLanguage";
import {
  Package,
  DollarSign,
  Warehouse,
  Store,
  FileText,
  FolderOpen,
  Image,
  Settings,
  ShoppingBag,
  Layers,
  AlertTriangle,
  Paintbrush,
  ArrowRightLeft,
} from "lucide-react";

// ─── Section metadata registry ──────────────────────────────
type SectionMeta = {
  icon: LucideIcon;
  color: string;
  subtitle?: string;
};

/**
 * Map section titles (or IDs) to their icon + color.
 * Keys are matched case-insensitively against the section label.
 */
const SECTION_META: Record<string, SectionMeta> = {
  "product details":       { icon: Package,        color: "blue",   subtitle: "Name, SKU, category, and brand" },
  "product images & overview": { icon: Image,      color: "teal",   subtitle: "Gallery and product media" },
  "pricing":               { icon: DollarSign,     color: "green",  subtitle: "Standard rates and price lists" },
  "inventory settings":    { icon: Warehouse,      color: "orange", subtitle: "Stock levels and valuation" },
  "inventory":             { icon: Warehouse,      color: "orange", subtitle: "Stock levels and valuation" },
  "storefront":            { icon: Store,          color: "purple", subtitle: "Online store visibility and URL" },
  "product documents":     { icon: FileText,       color: "red",    subtitle: "Datasheets, manuals, certificates" },
  "documents":             { icon: FileText,       color: "red",    subtitle: "Datasheets, manuals, certificates" },
  "collections":           { icon: FolderOpen,     color: "teal",   subtitle: "Product groupings" },
  "marketplace listings":  { icon: ShoppingBag,    color: "indigo", subtitle: "Connected marketplace channels" },
  "personalization":       { icon: Paintbrush,     color: "purple", subtitle: "Custom text, options, and file uploads" },
  "bundle components":     { icon: Package,        color: "indigo", subtitle: "Items inside this bundle" },
  "advanced":              { icon: Settings,       color: "gray",   subtitle: "Rarely-used settings" },
  "general":               { icon: Layers,         color: "gray",   subtitle: "Additional item details" },
  "disabled banner":       { icon: AlertTriangle,  color: "red" },
  // Item Customization Template sections
  "template info":         { icon: Package,        color: "blue",   subtitle: "Name, description, and status" },
  "fields":                { icon: Layers,         color: "teal",   subtitle: "What customers fill in" },
  "rules":                 { icon: Settings,       color: "orange", subtitle: "Conditional field logic" },
  "linked products":       { icon: ShoppingBag,    color: "indigo", subtitle: "Products using this template" },
  // Stock Entry wizard sections
  "stock entry type":      { icon: ArrowRightLeft, color: "blue",   subtitle: "What kind of stock movement" },
  "warehouse & details":   { icon: Warehouse,      color: "orange", subtitle: "Where items move and when" },
  "items":                 { icon: Package,         color: "green",  subtitle: "Products in this entry" },
  // Tab-level sections (Inventory, Accounting, etc.) use generic fallback
};

/** Resolve section metadata from label. Exported for potential future use by callers. */
export function getMeta(label: string): SectionMeta {
  const key = label.toLowerCase();
  return SECTION_META[key] ?? { icon: Settings, color: "gray" };
}

// ─── Section label translations ─────────────────────────────
const SECTION_LABEL_TH: Record<string, string> = {
  "overview": "ภาพรวม",
  "product images & overview": "รูปภาพและภาพรวมสินค้า",
  "product details": "รายละเอียดสินค้า",
  "pricing": "ราคา",
  "storefront": "หน้าร้านค้า",
  "marketplace listings": "รายการตลาดออนไลน์",
  "product documents": "เอกสารสินค้า",
  "personalization": "ปรับแต่งสินค้า",
  "bundle product": "สินค้าชุดรวม",
  "bundle components": "ส่วนประกอบชุดรวม",
  "variants": "ตัวเลือกสินค้า",
  "collections": "คอลเลกชัน",
  "inventory settings": "การตั้งค่าคลังสินค้า",
  "inventory": "คลังสินค้า",
  "advanced": "ขั้นสูง",
  "general": "ทั่วไป",
  "template info": "ข้อมูลเทมเพลต",
  "fields": "ฟิลด์",
  "rules": "กฎ",
  "linked products": "สินค้าที่เชื่อมโยง",
  "stock entry type": "ประเภทรายการสต็อก",
  "warehouse & details": "คลังสินค้าและรายละเอียด",
  "items": "รายการสินค้า",
};

function translateLabel(label: string, language: string): string {
  if (language !== "th") return label;
  return SECTION_LABEL_TH[label.toLowerCase()] ?? label;
}

// ─── Component ──────────────────────────────────────────────
const TEXT_SECTION_HEADER: Record<DetailTextSize, string> = {
  small: "text-[11px]",
  medium: "text-xs",
  large: "text-sm",
};

interface SectionHeaderProps {
  /** Section title text */
  label: string;
  /** Override subtitle (if not set, uses registry default) */
  subtitle?: string;
  /** Hide subtitle entirely */
  hideSubtitle?: boolean;
  /** Override icon */
  icon?: LucideIcon;
  /** Override color key */
  color?: string;
  /** Text size from density preference */
  textSize?: DetailTextSize;
}

export function SectionHeader({
  label,
  // These props are accepted for API compatibility but currently unused in simplified render
  subtitle: _subtitleOverride,
  hideSubtitle: _hideSubtitle,
  icon: _iconOverride,
  color: _colorOverride,
  textSize = "medium",
}: SectionHeaderProps) {
  void _subtitleOverride; void _hideSubtitle; void _iconOverride; void _colorOverride;
  const { language } = useLanguage();
  const translatedLabel = translateLabel(label, language);

  return (
    <div className="pl-1 mb-2">
      <h3 className={`${TEXT_SECTION_HEADER[textSize]} font-semibold text-muted-foreground uppercase tracking-wider`}>
        {translatedLabel}
      </h3>
    </div>
  );
}
